import { apiClient } from './apiClient';

export type ServiceCatalogItem = {
  id: string;
  name: string;
  description: string;
  formSchemaJson?: string | null;
  responseTemplate?: string | null;
  defaultSlaId?: string | null;
  defaultSlaName?: string | null;
  defaultAssignedTeamId?: string | null;
  defaultAssignedTeamName?: string | null;
  defaultPriority: string;
  autoFulfill: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ServiceCatalogItemListResult = {
  items: ServiceCatalogItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ServiceCatalogItemUpsertPayload = {
  name: string;
  description?: string;
  formSchemaJson?: string;
  responseTemplate?: string;
  defaultSlaId?: string;
  defaultAssignedTeamId?: string;
  defaultPriority?: string;
  autoFulfill?: boolean;
  isActive?: boolean;
  sortOrder?: number;
};

export type CatalogFormField = {
  name: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select';
  required?: boolean;
  options?: string[];
};

export type CatalogFormSchema = {
  fields: CatalogFormField[];
};

export function parseCatalogFormSchema(json?: string | null): CatalogFormSchema | null {
  if (!json?.trim()) return null;
  try {
    const parsed = JSON.parse(json) as CatalogFormSchema;
    if (!Array.isArray(parsed.fields)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function listServiceCatalogItems(params?: {
  activeOnly?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ServiceCatalogItemListResult> {
  const response = await apiClient.get<ServiceCatalogItemListResult>(
    '/v1/customer-success/service-catalog',
    { params },
  );
  return response.data;
}

export async function listActiveServiceCatalogItems(): Promise<ServiceCatalogItem[]> {
  const response = await apiClient.get<ServiceCatalogItem[]>(
    '/v1/customer-success/service-catalog/active',
  );
  return response.data;
}

export async function createServiceCatalogItem(
  payload: ServiceCatalogItemUpsertPayload,
): Promise<ServiceCatalogItem> {
  const response = await apiClient.post<ServiceCatalogItem>(
    '/v1/customer-success/service-catalog',
    payload,
  );
  return response.data;
}

export async function updateServiceCatalogItem(
  id: string,
  payload: ServiceCatalogItemUpsertPayload,
): Promise<ServiceCatalogItem> {
  const response = await apiClient.put<ServiceCatalogItem>(
    `/v1/customer-success/service-catalog/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteServiceCatalogItem(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/service-catalog/${id}`);
}
